/**
 * Property & Water System info card — read-only display on the Passport
 * property page. Owner can click "Edit" to open the full intake form modal.
 */
import { useEffect, useState } from "react";
import { Home, Droplet, ShieldCheck, Edit3, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PropertyIntakeForm, intakeFormToPayload, validateIntakeForm } from "@/components/PropertyIntakeForm";

export function PropertyInfoCard({ propertyId, canEdit = true, dataTestId = "property-info-card" }) {
  const [intake, setIntake] = useState(null);
  const [human, setHuman] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get(`/properties/${propertyId}/intake`).then(({ data }) => {
      setIntake(data.intake);
      setHuman(data.intake_human);
    }).catch(() => { setIntake(null); setHuman(null); });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [propertyId]);

  const startEdit = () => {
    setForm(intake || {});
    setEditing(true);
  };

  const save = async () => {
    const err = validateIntakeForm(form);
    if (err) return toast.error(err);
    setSaving(true);
    try {
      const payload = intakeFormToPayload(form);
      await api.put(`/properties/${propertyId}/intake`, payload);
      toast.success("Property info updated");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  if (!human && !canEdit) return null;
  return (
    <>
      <div data-testid={dataTestId} className="rounded-3xl bg-white border border-slate-200 p-6 lg:p-7">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700 flex items-center gap-1.5">
              <Home className="w-3.5 h-3.5" /> Property & Water System
            </div>
            <h3 className="font-display text-xl font-bold text-slate-900 mt-1.5">Site information</h3>
          </div>
          {canEdit && (
            <Button
              onClick={startEdit} size="sm" variant="outline"
              data-testid="property-info-edit-btn"
              className="rounded-full text-xs"
            >
              <Edit3 className="w-3.5 h-3.5 mr-1" /> {human ? "Edit" : "Add info"}
            </Button>
          )}
        </div>

        {!human ? (
          <p className="mt-4 text-sm text-slate-500">No property information captured yet. Adding details helps us tailor your recommendations and AI summaries.</p>
        ) : (
          <div className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {human.tanks && tankSummary(human.tanks) && <Row icon={Droplet} label="Water tanks" value={tankSummary(human.tanks)} fullWidth />}
            <Row icon={ShieldCheck} label="Filtration" value={human.filtration} />
            {human.uv && human.uv !== "—" && <Row label="UV treatment" value={human.uv} />}
            {human.calcite && human.calcite !== "—" && <Row label="Calcite filter" value={human.calcite} />}
            {/* `filter_last_replaced` row removed Feb-2026 — question no longer asked. */}
            {human.tank_cleaned_5y && human.tank_cleaned_5y !== "—" && <Row label="Tank cleaned in last 5y" value={human.tank_cleaned_5y} />}
            <Row icon={Home} label="Home age" value={human.home_age} />
            {human.experienced_issues?.length > 0 && (
              <Row label="Experienced issues" value={human.experienced_issues.join(", ")} fullWidth />
            )}
            {human.notes && <Row label="Notes" value={human.notes} fullWidth />}
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm overflow-y-auto py-8">
          <div className="max-w-3xl mx-auto px-4">
            <div className="bg-white rounded-3xl shadow-2xl">
              <div className="flex items-center justify-between gap-3 px-6 py-5 border-b border-slate-100">
                <h3 className="font-display text-lg font-bold text-slate-900">Edit Property & Water System Information</h3>
                <button onClick={() => setEditing(false)} className="p-1.5 rounded-full hover:bg-slate-100" data-testid="property-info-edit-close">
                  <X className="w-4 h-4 text-slate-500" />
                </button>
              </div>
              <div className="px-6 py-6">
                <PropertyIntakeForm value={form} onChange={setForm} initial={form} embedded testIdPrefix="property-info-edit" />
              </div>
              <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
                <Button onClick={() => setEditing(false)} variant="outline" className="rounded-full">Cancel</Button>
                <Button onClick={save} disabled={saving} className="rounded-full bg-slate-900 hover:bg-cyan-700 text-white" data-testid="property-info-save-btn">
                  {saving ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


function Row({ icon: Icon, label, value, fullWidth }) {
  if (value == null || value === "" || value === "—") return null;
  return (
    <div className={fullWidth ? "sm:col-span-2" : ""}>
      <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="text-sm text-slate-900 mt-0.5">{String(value)}</div>
    </div>
  );
}


function tankSummary(t) {
  if (!t) return null;
  if (t.none) return "No water tanks on this property";
  const parts = [];
  if (t.poly) parts.push(`${t.poly} poly`);
  if (t.concrete) parts.push(`${t.concrete} concrete`);
  if (t.steel) parts.push(`${t.steel} steel`);
  if (t.fibreglass) parts.push(`${t.fibreglass} fibreglass`);
  if (t.other) parts.push(`${t.other} other${t.other_description ? ` (${t.other_description})` : ""}`);
  return parts.length ? parts.join(", ") : null;
}
